import { DestroyRef, inject, Injectable, signal } from '@angular/core';

/**
 * BreakpointService — Reactive breakpoint detection using matchMedia.
 *
 * Exposes boolean signals for common breakpoints (mobile, tablet, desktop).
 * Uses the same breakpoints as Tailwind CSS: sm=640, md=768, lg=1024.
 *
 * @example
 * readonly bp = inject(BreakpointService);
 * readonly isMobile = this.bp.isMobile; // Signal<boolean>
 */
@Injectable({
  providedIn: 'root',
})
export class BreakpointService {
  private readonly destroyRef = inject(DestroyRef);

  private readonly mobileQuery = window.matchMedia('(max-width: 767px)');
  private readonly tabletQuery = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');
  private readonly desktopQuery = window.matchMedia('(min-width: 1024px)');

  /** True when viewport width < 768px (Tailwind md breakpoint) */
  readonly isMobile = signal(this.mobileQuery.matches);
  /** True when viewport width >= 768px and < 1024px */
  readonly isTablet = signal(this.tabletQuery.matches);
  /** True when viewport width >= 1024px */
  readonly isDesktop = signal(this.desktopQuery.matches);

  constructor() {
    const sync = () => {
      this.isMobile.set(this.mobileQuery.matches);
      this.isTablet.set(this.tabletQuery.matches);
      this.isDesktop.set(this.desktopQuery.matches);
    };

    this.mobileQuery.addEventListener('change', sync);
    this.tabletQuery.addEventListener('change', sync);
    this.desktopQuery.addEventListener('change', sync);

    this.destroyRef.onDestroy(() => {
      this.mobileQuery.removeEventListener('change', sync);
      this.tabletQuery.removeEventListener('change', sync);
      this.desktopQuery.removeEventListener('change', sync);
    });
  }
}
